<template>
  <div>
    <el-table
      :border="true"
      :data="rows"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      v-loading="loading"
    >
      <el-table-column type="selection" width="55"></el-table-column>

      <el-table-column v-if="hasPermissionToAuditLogs" :label="fields.id.label"  :prop="fields.id.name" sortable="custom">
        <template slot-scope="scope">{{ presenter(scope.row, 'id') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.type.label"
        :prop="fields.type.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'type') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.subtypeGame.label"
        :prop="fields.subtypeGame.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'subtypeGame') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.subtypeGoal.label"
        :prop="fields.subtypeGoal.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'subtypeGoal') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.subtypeCard.label"
        :prop="fields.subtypeCard.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'subtypeCard') }}</template>
      </el-table-column>

      <el-table-column
        :label="fields.subtypePenaltyShootout.label"
        :prop="fields.subtypePenaltyShootout.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'subtypePenaltyShootout') }}</template>
      </el-table-column>

      <el-table-column :label="fields.teamId.label" :prop="fields.teamId.name">
        <template slot-scope="scope">
          <app-list-item-relation-to-one
            :label="fields.teamId.label"
            :permission="fields.teamId.readPermission"
            :url="fields.teamId.viewUrl"
            :value="presenter(scope.row, 'teamId')"
          ></app-list-item-relation-to-one>
        </template>
      </el-table-column>

      <el-table-column
        :label="fields.minute.label"
        :prop="fields.minute.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'minute') }}</template>
      </el-table-column>

      <el-table-column :label="fields.player.label" :prop="fields.player.name">
        <template slot-scope="scope">
          <app-list-item-relation-to-one
            :label="fields.player.label"
            :permission="fields.player.readPermission"
            :url="fields.player.viewUrl"
            :value="presenter(scope.row, 'player')"
          ></app-list-item-relation-to-one>
        </template>
      </el-table-column>

      <el-table-column :label="fields.otherTeamId.label" :prop="fields.otherTeamId.name">
        <template slot-scope="scope">
          <app-list-item-relation-to-one
            :label="fields.otherTeamId.label"
            :permission="fields.otherTeamId.readPermission"
            :url="fields.otherTeamId.viewUrl"
            :value="presenter(scope.row, 'otherTeamId')"
          ></app-list-item-relation-to-one>
        </template>
      </el-table-column>

      <el-table-column
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        sortable="custom"
      >
        <template slot-scope="scope">{{ presenter(scope.row, 'createdAt') }}</template>
      </el-table-column>

      <el-table-column :fixed="isMobile? undefined : 'right'" align="center" width="180">
        <template slot-scope="scope">
          <div class="table-actions">
            <router-link :to="`/sheet-event/${scope.row.id}`">
              <el-button type="text">
                <app-i18n code="common.view"></app-i18n>
              </el-button>
            </router-link>

            <router-link :to="`/sheet-event/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button type="text">
                <app-i18n code="common.edit"></app-i18n>
              </el-button>
            </router-link>

            <el-button
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              type="text"
              v-if="hasPermissionToDestroy"
            >
              <app-i18n code="common.destroy"></app-i18n>
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { SheetEventModel } from '@modules/sheet-event/sheet-event-model';
import { mapGetters, mapActions } from 'vuex';
import { SheetEventPermissions } from '@modules/sheet-event/sheet-event-permissions';
import { i18n } from '@shared/i18n';

const { fields } = SheetEventModel;

export default {
  name: 'app-sheet-event-list-table',

  mounted() {
    this.doMountTable(this.$refs.table);
  },

  computed: {
    ...mapGetters({
      rows: 'sheetEvent/list/rows',
      count: 'sheetEvent/list/count',
      loading: 'sheetEvent/list/loading',
      pagination: 'sheetEvent/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      destroyLoading: 'sheetEvent/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new SheetEventPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new SheetEventPermissions(this.currentUser).destroy;
    },

    fields() {
      return fields;
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'sheetEvent/list/doChangeSort',
      doChangePaginationCurrentPage:
        'sheetEvent/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'sheetEvent/list/doChangePaginationPageSize',
      doMountTable: 'sheetEvent/list/doMountTable',
      doDestroy: 'sheetEvent/destroy/doDestroy',
    }),

    presenter(row, fieldName) {
      return SheetEventModel.presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },
  },
};
</script>

<style>
</style>
